import { useEffect, useMemo, useState } from 'react';
import { fmtDate } from '@/lib/dates';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Calendar, Check, CheckCircle2, ClipboardList, EyeOff, FileText, Hourglass, Info, LayoutGrid, LayoutList, Loader2, Lock, Plus, Search, ShieldAlert, UserPlus, X, XCircle } from 'lucide-react';
import { AddStaffRowDialog } from './AddStaffRowDialog';
import { DoaBinderScopeBadge } from '@/components/binder/BlindingScopeBadge';
import { useDoaSignatureSelfHeal } from '@/hooks/useDoaSignatureSelfHeal';
import {
  doa,
  ApiError,
  type DoaGridResponse,
  type DoaGridRow,
  type DoaQualifyingDoc,
  type DoaQualificationStatus,
} from '@/api/client';
import { Send } from 'lucide-react';
import { humanRole } from '@/lib/humanise';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '@/components/ui/UnsavedChangesModal';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Sprint-17 — DoA grid editor (standard industry matrix layout).
 *
 * Full-screen modal-ish surface. Sticky-left staff column, rotated
 * responsibility column headers, checkbox per (person, responsibility)
 * cell. Local state for ticks; saves on explicit "Save" click. Read-only
 * for non-draft versions.
 *
 * Qualification chip per row opens a modal listing the qualifying
 * documents in the Staff folder (computed from owned binder slots).
 */

const QUAL_META: Record<
  DoaQualificationStatus,
  { label: string; cls: string; icon: React.ReactNode; tooltip: string }
> = {
  qualified: {
    label: 'Qualified',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <CheckCircle2 className="h-3 w-3" />,
    tooltip: 'All qualifying documents are current.',
  },
  doc_expiring: {
    label: 'Doc expiring',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: <Hourglass className="h-3 w-3" />,
    tooltip: 'At least one qualifying document is expiring within 30 days.',
  },
  qualification_gap: {
    label: 'Qualification gap',
    cls: 'bg-danger-50 text-danger-700 border-danger-200',
    icon: <ShieldAlert className="h-3 w-3" />,
    tooltip: 'At least one qualifying document is expired. Resolve before delegating.',
  },
  no_docs_on_file: {
    label: 'No docs on file',
    cls: 'bg-slate-50 text-slate-700 border-slate-200',
    icon: <FileText className="h-3 w-3" />,
    tooltip: 'No qualifying documents in the Staff folder for this person.',
  },
};

export default function DoaGridEditor() {
  const { binderId: binderRef = '' } = useParams<{ binderId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['doa-grid', binderRef],
    queryFn: () => doa.getGrid(binderRef),
    enabled: !!binderRef,
    // Bug found live (UAT: "DOA Logs: Signature issue not fixed yet") —
    // this is the PRIMARY DoA page (linked from BinderDetailPage,
    // DelegationsIndexPage, StudyWorkspaceHubPage) but had NO refresh
    // mechanism at all, unlike the secondary DoaVersionDetail page's own
    // "poll while in_review" (Phase 2 Defect 2.6). Matches that pattern.
    refetchInterval: (query) => (query.state.data?.version?.status === 'in_review' ? 10_000 : false),
  });

  // Self-heal against DocuSeal directly — the poll above only re-reads
  // Caspio, so if the webhook never landed (or silently failed), a
  // row/PI signature stays "Pending Signature" forever with nothing to
  // notice. See useDoaSignatureSelfHeal.ts.
  useDoaSignatureSelfHeal(
    binderRef,
    q.data?.version?.doa_version_id ?? '',
    q.data?.version?.status === 'in_review',
  );

  // Local checkbox state — keyed by row_id, value = Set of codes.
  // Initialised from the API response; saved on explicit click.
  const [localTicks, setLocalTicks] = useState<Map<string, Set<string>>>(new Map());
  // DT2-BLIND-3 — per-row blinded/unblinded pick. Only tracked for rows on a
  // blinded study; saved alongside the tick edits.
  const [localBlinded, setLocalBlinded] = useState<Map<string, boolean>>(new Map());
  const [search, setSearch] = useState('');
  const [qualModal, setQualModal] = useState<DoaGridRow | null>(null);
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  // Feedback #20 - Cards view is a per-staff redesign of the delegation
  // input. The 20+ column rotated-header matrix is overwhelming when
  // you're only ticking one person's responsibilities; the card view
  // shows one staff member at a time with a searchable checklist.
  // Persisted in localStorage so the user's preference sticks across
  // sessions.
  const [viewMode, setViewMode] = useState<'matrix' | 'cards'>(() => {
    if (typeof window === 'undefined') return 'matrix';
    return (window.localStorage.getItem('doa-grid-view-mode') as 'matrix' | 'cards') ?? 'matrix';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('doa-grid-view-mode', viewMode);
    }
  }, [viewMode]);

  useEffect(() => {
    if (q.data) {
      const next = new Map<string, Set<string>>();
      const nextBlinded = new Map<string, boolean>();
      for (const r of q.data.rows) {
        next.set(r.doa_row_id, new Set(r.delegated_codes));
        if (r.blinded !== null) nextBlinded.set(r.doa_row_id, r.blinded);
      }
      setLocalTicks(next);
      setLocalBlinded(nextBlinded);
    }
  }, [q.data]);

  // Compute dirty rows for the save payload.
  const dirty = useMemo(() => {
    if (!q.data) return { rows: [], anyChange: false };
    const rows: Array<{ doa_row_id: string; responsibility_codes: string[]; blinded?: boolean }> = [];
    let anyChange = false;
    for (const r of q.data.rows) {
      const baseline = new Set(r.delegated_codes);
      const current = localTicks.get(r.doa_row_id) ?? new Set<string>();
      const codes = [...current];
      const changed =
        baseline.size !== current.size ||
        codes.some((c) => !baseline.has(c)) ||
        [...baseline].some((c) => !current.has(c));
      const curBlinded = localBlinded.get(r.doa_row_id);
      const blindedChanged = curBlinded !== undefined && curBlinded !== r.blinded;
      if (changed || blindedChanged) anyChange = true;
      rows.push({
        doa_row_id: r.doa_row_id,
        responsibility_codes: codes.sort(),
        ...(blindedChanged ? { blinded: curBlinded } : {}),
      });
    }
    return { rows, anyChange };
  }, [q.data, localTicks, localBlinded]);

  // Phase 2 Defect 2.5 sibling sweep — protect uncommitted ticks from
  // silent loss on navigation away / tab close. `dirty.anyChange`
  // already models the "is dirty" invariant we need.
  const unsavedGuard = useUnsavedChangesGuard(dirty.anyChange);

  const save = useMutation({
    mutationFn: () => {
      if (!q.data?.version) throw new Error('No DoA version on the binder');
      return doa.saveDelegations(binderRef, {
        doa_version_id: q.data.version.doa_version_id,
        rows: dirty.rows,
      });
    },
    onSuccess: (r) => {
      const totals = r.changes.reduce(
        (acc, c) => ({
          added: acc.added + c.added.length,
          removed: acc.removed + c.removed.length,
        }),
        { added: 0, removed: 0 },
      );
      toast.success(`Saved — ${totals.added} delegations added, ${totals.removed} removed`);
      qc.invalidateQueries({ queryKey: ['doa-grid', binderRef] });
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Save failed'));
    },
  });

  /**
   * Generate Delegation Log: persist the current grid state THEN initiate
   * signing envelopes (per-row OTP + PI countersign, Sprint 10 machinery).
   * Save runs first so the BE's signing flow sees the final delegations.
   */
  const generate = useMutation({
    mutationFn: async () => {
      if (!q.data?.version) throw new Error('No DoA version on the binder');
      const versionId = q.data.version.doa_version_id;
      // Save first if there are pending changes; otherwise skip straight
      // to initiate so we don't write a no-op audit row.
      if (dirty.anyChange) {
        await doa.saveDelegations(binderRef, {
          doa_version_id: versionId,
          rows: dirty.rows,
        });
      }
      return doa.initiate(binderRef, versionId);
    },
    onSuccess: (r) => {
      toast.success(
        `Generated — ${r.envelopes_created} signing envelopes created. Click Send Invitations to notify staff.`,
      );
      qc.invalidateQueries({ queryKey: ['doa-grid', binderRef] });
      qc.invalidateQueries({ queryKey: ['binder', binderRef] });
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Generate failed'));
    },
  });

  /**
   * 2026-07-30 Feedback item 2 — separate, explicit "Send Invitations"
   * step. Generate only mints envelopes; this is what actually emails
   * staff still pending signature. Safe to click more than once.
   */
  const sendInvitations = useMutation({
    mutationFn: () => {
      if (!q.data?.version) throw new Error('No DoA version on the binder');
      return doa.sendInvitations(binderRef, q.data.version.doa_version_id);
    },
    onSuccess: (r) => {
      toast.success(
        r.sent_count > 0
          ? `Sent ${r.sent_count} invitation${r.sent_count === 1 ? '' : 's'}${r.skipped_count ? ` (${r.skipped_count} already signed/no action needed)` : ''}.`
          : 'No pending invitations to send.',
      );
      qc.invalidateQueries({ queryKey: ['doa-grid', binderRef] });
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Send invitations failed'));
    },
  });

  /**
   * End date (Feedback DoA item 1). Saved per row on change rather than
   * folded into `dirty`/saveDelegations — that endpoint carries
   * responsibility codes and the blinding flag only, and widening it would
   * make every tick a date write too.
   *
   * Caspio reads lag their own writes by a second or two, so an invalidate
   * here refetches the OLD value and the field visibly snaps back. Patch the
   * cached row instead and let the next natural refetch reconcile.
   */
  const setEndDate = useMutation({
    mutationFn: ({ rowId, iso }: { rowId: string; iso: string | null }) => {
      if (!q.data?.version) throw new Error('No DoA version on the binder');
      return doa.updateRow(binderRef, q.data.version.doa_version_id, rowId, {
        effective_to: iso,
      });
    },
    onSuccess: (_r, vars) => {
      qc.setQueryData(['doa-grid', binderRef], (prev: DoaGridResponse | undefined) =>
        prev
          ? {
              ...prev,
              rows: prev.rows.map((r) =>
                r.doa_row_id === vars.rowId ? { ...r, effective_to: vars.iso } : r,
              ),
            }
          : prev,
      );
      toast.success(vars.iso ? 'End date updated' : 'End date cleared');
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Could not update the end date'));
      qc.invalidateQueries({ queryKey: ['doa-grid', binderRef] });
    },
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading delegation grid…
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className={`m-6 flex items-start gap-2 rounded border p-4 text-sm ${errorToneClass(q.error)}`}>
        {isPermissionError(q.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>{errorText(q.error, 'Failed to load grid.')}</span>
      </div>
    );
  }

  if (!q.data) return null;

  const data: DoaGridResponse = q.data;

  // Sprint-18 — empty-state for binders that have no DoA version yet.
  // Auto-gen only runs at study activation; binders activated before
  // Sprint 17, or activated with no staff at the time, end up here.
  if (data.version === null) {
    return (
      <EmptyDoaState
        binderRef={binderRef}
        canCreate={data.can_create}
        onCreated={() => qc.invalidateQueries({ queryKey: ['doa-grid', binderRef] })}
      />
    );
  }

  const filteredRows = search.trim()
    ? data.rows.filter((r) =>
        (r.staff_full_name + ' ' + r.role).toLowerCase().includes(search.trim().toLowerCase()),
      )
    : data.rows;
  const responsibilities = [...data.responsibilities].sort((a, b) => a.sort_order - b.sort_order);
  const missingDesignation = data.is_blinded_study
    ? data.rows.filter(
        (r) =>
          r.row_status !== 'revoked' && (localBlinded.get(r.doa_row_id) ?? r.blinded) === null,
      )
    : [];

  function toggleCell(rowId: string, code: string, rowSigned: boolean) {
    if (!data.is_editable || rowSigned) return;
    setLocalTicks((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(rowId) ?? []);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      next.set(rowId, set);
      return next;
    });
  }

  function setBlinded(rowId: string, value: boolean, rowSigned: boolean) {
    if (!data.is_editable || rowSigned) return;
    setLocalBlinded((prev) => {
      const next = new Map(prev);
      next.set(rowId, value);
      return next;
    });
  }

  return (
    <div className="flex flex-col h-full bg-surface-muted/10">
      {/* Dark top bar — matches the demo's full-bleed header */}
      <header className="bg-ink-primary text-white px-5 py-3 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList className="h-5 w-5 text-accent-300" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-base font-semibold leading-tight">
              Delegation of Authority
              <DoaBinderScopeBadge binderRef={binderRef} />
            </div>
            <div className="text-[11px] text-white/60 leading-tight">
              v{data.version.version_number} · {data.version.status}
              {data.version.reference_number && (
                <>
                  {' '}
                  · <span className="font-mono">{data.version.reference_number}</span>
                </>
              )}
              {!data.is_editable && (
                <span className="ml-2 inline-flex items-center gap-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px]">
                  <Lock className="h-2.5 w-2.5" /> Read-only
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/binder/${encodeURIComponent(binderRef)}/doa`)}
            className="inline-flex items-center gap-1.5 rounded bg-white/10 px-3 h-9 text-sm font-medium text-white hover:bg-white/20"
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded bg-accent-500 px-3.5 h-9 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-60"
            disabled={
              !data.is_editable || save.isPending || generate.isPending || data.rows.length === 0
            }
            onClick={() => {
              if (missingDesignation.length > 0) {
                toast.error(
                  `Every staff row needs a Blinded or Unblinded designation, set on the study team (Assign Staff). Missing: ${missingDesignation
                    .map((r) => r.staff_full_name || r.staff_email)
                    .join(', ')}`,
                );
                return;
              }
              generate.mutate();
            }}
            title={
              !data.is_editable
                ? 'Read-only version'
                : data.rows.length === 0
                  ? 'Add at least one staff row before generating'
                  : 'Save current state and dispatch signing envelopes (per-row + PI countersign)'
            }
          >
            {generate.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Generate Delegation Log
          </button>
          {data.version.status === 'in_review' && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded bg-emerald-600 px-3.5 h-9 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
              disabled={sendInvitations.isPending}
              onClick={() => sendInvitations.mutate()}
              title="Email every staff row still pending signature"
            >
              {sendInvitations.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              Send Invitations
            </button>
          )}
        </div>
      </header>

      {/* Tab strip — matches demo's Overview / Delegations split */}
      <nav className="bg-white border-b border-surface-border px-5 flex items-center gap-6">
        <Link
          to={`/binder/${encodeURIComponent(binderRef)}/doa`}
          className="text-sm text-ink-muted hover:text-ink-primary py-3 border-b-2 border-transparent"
        >
          Overview
        </Link>
        <div className="text-sm text-ink-primary font-semibold py-3 border-b-2 border-accent-500">
          Delegations
        </div>
      </nav>

      {/* Action strip — Save, Add Staff, Search */}
      <div className="bg-white border-b border-surface-border px-5 py-2.5 flex items-center gap-2 flex-wrap">
        <div className="text-sm font-semibold text-ink-primary">Delegations</div>
        {/* Feedback #20 - matrix vs cards toggle */}
        <div className="ml-4 inline-flex rounded border border-surface-border bg-white p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('matrix')}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${
              viewMode === 'matrix'
                ? 'bg-ink-primary text-white'
                : 'text-ink-secondary hover:bg-surface-muted/60'
            }`}
            title="Wide matrix - every staff x responsibility at once"
          >
            <LayoutGrid className="h-3 w-3" />
            Matrix
          </button>
          <button
            type="button"
            onClick={() => setViewMode('cards')}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${
              viewMode === 'cards'
                ? 'bg-ink-primary text-white'
                : 'text-ink-secondary hover:bg-surface-muted/60'
            }`}
            title="One card per staff member with a checklist"
          >
            <LayoutList className="h-3 w-3" />
            Cards
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-2 rounded border border-surface-border bg-white px-2.5 h-9 min-w-[220px]">
            <Search className="h-3.5 w-3.5 text-ink-muted shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff…"
              className="flex-1 bg-transparent outline-none text-sm text-ink-primary placeholder:text-ink-muted"
            />
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded border border-surface-border bg-white px-3.5 h-9 text-sm font-medium text-ink-primary hover:bg-surface-muted/50 disabled:opacity-60"
            disabled={!data.is_editable || save.isPending || generate.isPending}
            onClick={() => setAddStaffOpen(true)}
            title={
              !data.is_editable ? 'Read-only version' : 'Add a staff member to this DoA version'
            }
          >
            <UserPlus className="h-3.5 w-3.5" />
            Add staff
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded bg-ink-primary px-3.5 h-9 text-sm font-medium text-white hover:bg-ink-primary/90 disabled:opacity-60"
            disabled={!data.is_editable || !dirty.anyChange || save.isPending || generate.isPending}
            onClick={() => save.mutate()}
            title={
              !data.is_editable ? 'Read-only version' : !dirty.anyChange ? 'No changes to save' : ''
            }
          >
            {save.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Save (Draft)
          </button>
        </div>
      </div>

      {/* Key legend — matches demo's "Key: ✓ Delegated" hint */}
      <div className="bg-white border-b border-surface-border px-5 py-1.5 text-[11px] text-ink-muted flex items-center gap-2">
        <span className="font-medium text-ink-secondary">Key:</span>
        <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-emerald-600 bg-emerald-600 text-white">
          <Check className="h-2.5 w-2.5" />
        </span>
        Delegated
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto bg-surface-muted/20">
        {data.rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink-muted">
            <div>No staff rows on this DoA version yet.</div>
            {data.is_editable && (
              <button
                type="button"
                onClick={() => setAddStaffOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 rounded bg-accent-600 px-3.5 h-9 text-sm font-medium text-white hover:bg-accent-700"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Add first staff member
              </button>
            )}
            <div className="mt-4 text-[11px] text-ink-muted">
              Tip: auto-generation populates rows from Study Onboarding staff at activation — if
              this DoA was created before that hook landed, add staff manually here.
            </div>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredRows.map((row) => (
              <StaffDelegationCard
                key={row.doa_row_id}
                row={row}
                responsibilities={responsibilities}
                ticks={localTicks.get(row.doa_row_id) ?? new Set<string>()}
                isEditable={data.is_editable}
                showBlinding={data.is_blinded_study}
                blinded={localBlinded.get(row.doa_row_id) ?? row.blinded}
                onToggle={(code) => toggleCell(row.doa_row_id, code, row.row_status === 'signed')}
                onBlindedChange={(v) => setBlinded(row.doa_row_id, v, row.row_status === 'signed')}
                onOpenQual={() => setQualModal(row)}
                onEndDateChange={(iso) => setEndDate.mutate({ rowId: row.doa_row_id, iso })}
                endDateSaving={
                  setEndDate.isPending && setEndDate.variables?.rowId === row.doa_row_id
                }
              />
            ))}
          </div>
        ) : (
          <div className="inline-block min-w-full">
            <table className="border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="sticky left-0 z-30 bg-white border-b border-r border-surface-border px-3 py-2 text-left text-[11px] uppercase tracking-wider text-ink-muted min-w-[260px]">
                    Study Team
                  </th>
                  <th className="bg-white border-b border-r border-surface-border px-3 py-2 text-left text-[11px] uppercase tracking-wider text-ink-muted min-w-[160px]">
                    Study Role
                  </th>
                  <th className="bg-white border-b border-r border-surface-border px-3 py-2 text-left text-[11px] uppercase tracking-wider text-ink-muted min-w-[170px]">
                    Start Date
                  </th>
                  <th className="bg-white border-b border-r border-surface-border px-3 py-2 text-left text-[11px] uppercase tracking-wider text-ink-muted min-w-[170px]">
                    End Date
                  </th>
                  {responsibilities.map((r) => (
                    <th
                      key={r.code}
                      className="bg-white border-b border-r border-surface-border align-bottom relative"
                      style={{ height: 220, width: 56, minWidth: 56 }}
                      title={r.definition}
                    >
                      {/*
                        Rotated column header.

                        Geometry: pivot the label's BOTTOM-LEFT at the cell's
                        bottom-left (anchored via absolute positioning), then
                        rotate it -70° around that pivot (transformOrigin
                        0% 100%). The label extends up-and-right from there.

                        Why -70° and 220px header height:
                          - At 56px column width, anything shallower than
                            roughly -65° causes adjacent labels to overlap
                            (200px label × cos(45°) ≈ 141px horizontal extent
                            vs 56px cell). -70° gives ≈ 68px horizontal
                            extent — close enough to the cell width that
                            visual collision is minimal.
                          - At -70° the same label has ≈ 188px vertical
                            extent, so the header row needs ≥ 200px tall to
                            fully clear the longest label without clipping.
                          - leading-none kills the default line-height
                            inflation that would push the rotation pivot
                            inwards from the cell boundary.
                      */}
                      <div
                        className="absolute bottom-1 left-1 whitespace-nowrap text-[11px] leading-none text-ink-secondary"
                        style={{ transform: 'rotate(-70deg)', transformOrigin: '0% 100%' }}
                      >
                        {r.label}
                      </div>
                    </th>
                  ))}
                  <th className="bg-white border-b border-surface-border px-3 py-2 text-[11px] uppercase tracking-wider text-ink-muted">
                    Σ
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const ticks = localTicks.get(row.doa_row_id) ?? new Set<string>();
                  const isLocked = row.row_status === 'signed' || !data.is_editable;
                  return (
                    <tr key={row.doa_row_id} className="hover:bg-white">
                      <td className="sticky left-0 z-20 bg-white border-b border-r border-surface-border px-3 py-2 align-middle">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-ink-primary text-sm">
                            {row.staff_full_name || row.staff_email}
                          </span>
                          {data.is_blinded_study && (
                            <BlindedToggle
                              name={`blinded-${row.doa_row_id}`}
                              blinded={localBlinded.get(row.doa_row_id) ?? row.blinded}
                              disabled={isLocked}
                              onChange={(v) =>
                                setBlinded(row.doa_row_id, v, row.row_status === 'signed')
                              }
                            />
                          )}
                        </div>
                        {row.row_status === 'signed' && (
                          <div className="mt-1">
                            <span
                              className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700"
                              title="Row signed — delegations locked"
                            >
                              <Lock className="h-2.5 w-2.5" /> Signed
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="bg-white border-b border-r border-surface-border px-3 py-2 align-middle text-xs text-ink-secondary">
                        {humanRole(row.role)}
                      </td>
                      <td className="bg-white border-b border-r border-surface-border px-3 py-2 align-middle">
                        <DateChip iso={row.effective_from} />
                      </td>
                      <td className="bg-white border-b border-r border-surface-border px-3 py-2 align-middle">
                        <EditableDateChip
                          iso={row.effective_to}
                          disabled={isLocked}
                          placeholder="DD MMM YYYY"
                          saving={
                            setEndDate.isPending &&
                            setEndDate.variables?.rowId === row.doa_row_id
                          }
                          onChange={(iso) => setEndDate.mutate({ rowId: row.doa_row_id, iso })}
                        />
                      </td>
                      {responsibilities.map((r) => {
                        const checked = ticks.has(r.code);
                        return (
                          <td
                            key={r.code}
                            className="bg-white border-b border-r border-surface-border align-middle text-center cursor-pointer hover:bg-accent-50"
                            style={{ width: 56, minWidth: 56 }}
                            onClick={() => toggleCell(row.doa_row_id, r.code, isLocked)}
                          >
                            <div
                              className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${
                                checked
                                  ? 'border-emerald-600 bg-emerald-600 text-white'
                                  : 'border-surface-border bg-white'
                              } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {checked && <Check className="h-3 w-3" />}
                            </div>
                          </td>
                        );
                      })}
                      <td className="bg-white border-b border-surface-border px-3 py-2 align-middle text-center text-xs text-ink-muted font-mono">
                        {ticks.size}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer banner — surfaces unsaved changes */}
      {dirty.anyChange && data.is_editable && (
        <div className="bg-accent-50 border-t border-accent-100 px-5 py-2 text-xs text-accent-800 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" />
          Unsaved changes. Click <span className="font-semibold">Save delegations</span> to commit.
        </div>
      )}

      {qualModal && <QualificationModal row={qualModal} onClose={() => setQualModal(null)} />}

      <AddStaffRowDialog
        binderId={binderRef}
        versionId={data.version.doa_version_id}
        existingUserIds={data.rows.map((r) => r.staff_user_id)}
        open={addStaffOpen}
        onClose={() => setAddStaffOpen(false)}
        onAdded={() => {
          setAddStaffOpen(false);
          qc.invalidateQueries({ queryKey: ['doa-grid', binderRef] });
        }}
      />

      {/* Phase 2 Defect 2.5 sibling sweep — intercepts route change or
          tab close while the grid has uncommitted tick changes. */}
      <UnsavedChangesModal
        guard={unsavedGuard}
        title="Leave without saving delegations?"
        body="You have unsaved delegation changes on this DoA grid. If you leave now, your tick edits will be lost."
        proceedLabel="Discard & leave"
        stayLabel="Stay on grid"
      />
    </div>
  );
}

/**
 * Modal: shows the qualifying documents for a staff member (the click-
 * through from the row's qualification chip). Listed by document type
 * with per-doc status.
 */
function QualificationModal({ row, onClose }: { row: DoaGridRow; onClose: () => void }) {
  const meta = QUAL_META[row.qualification.status];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !m-0 p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-lg border border-surface-border bg-white p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-full border ${meta.cls}`}
          >
            {meta.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-ink-primary">{row.staff_full_name}</h3>
            <div className="text-xs text-ink-muted">
              {humanRole(row.role)} · {row.staff_email}
            </div>
            <div
              className={`mt-2 inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] ${meta.cls}`}
            >
              {meta.icon}
              {meta.label}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">
            Qualifying documents
          </div>
          {row.qualification.qualifying_docs.length === 0 ? (
            <div className="rounded border border-dashed border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted">
              No documents in the Staff folder owned by this person. Upload a CV, license, or
              training certificate to qualify.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {row.qualification.qualifying_docs.map((d) => (
                <QualifyingDocRow key={d.document_id} d={d} />
              ))}
            </ul>
          )}
        </div>
        <div className="mt-5 text-[11px] text-ink-muted">
          Tip: the qualification chip reflects the most-recent status of every owned slot. Replace
          expired docs via a new version to clear gaps.
        </div>
      </div>
    </div>
  );
}

/**
 * Yellow date chip matching the demo's `.dg-date` styling. Read-only for
 * v1 — the underlying fields (effective_from / effective_to) are set at
 * row creation; in-place editing would need a date-picker + a per-row
 * patch endpoint. Surfaces dates clearly so the demo parity is honest.
 */
/**
 * Sprint-18 — empty-state for binders that have no DoA version yet.
 * Auto-generation runs only at study activation; binders whose study was
 * activated before Sprint 17 shipped, or activated with no delegated staff,
 * land here. The Create button calls POST /binders/{id}/doa/versions which
 * mirrors the activation hook exactly (same source-of-truth, same row shape).
 */
function EmptyDoaState({
  binderRef,
  canCreate,
  onCreated,
}: {
  binderRef: string;
  canCreate: boolean;
  onCreated: () => void;
}) {
  const create = useMutation({
    mutationFn: () => doa.createFirstVersion(binderRef),
    onSuccess: (r) => {
      toast.success(
        r.created
          ? `Created DoA v1.0 with ${r.staff_count} staff member(s) from study onboarding`
          : 'DoA already exists — refreshing',
      );
      onCreated();
    },
    onError: (err) => {
      // Backend returns 409 when a DoA already exists (race condition or stale
      // empty-state cache). Treat this as success — just refresh the grid.
      if (err instanceof ApiError && err.message.toLowerCase().includes('already exists')) {
        toast.success('DoA already exists — refreshing grid');
        onCreated();
        return;
      }
      toast.error(errorText(err, 'Failed to create DoA'));
    },
  });

  return (
    <div className="flex flex-col h-full bg-surface-muted/10">
      <header className="bg-ink-primary text-white px-5 py-3 flex items-center gap-3 flex-wrap">
        <ClipboardList className="h-5 w-5 text-accent-300" />
        <div>
          <div className="text-base font-semibold leading-tight">Delegation of Authority</div>
          <div className="text-[11px] text-white/60 leading-tight">No version filed yet</div>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center p-10">
        <div className="max-w-lg text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center">
            <ClipboardList className="h-7 w-7 text-amber-600" />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-ink-primary">
            No Delegation of Authority on file
          </h2>
          <p className="mt-2 text-sm text-ink-muted">
            A DoA is normally auto-generated when a study is activated, using the staff already
            delegated during onboarding. This binder doesn't have one yet — typically because it was
            activated before auto-generation shipped, or because no staff were delegated at
            activation time.
          </p>
          <p className="mt-3 text-sm text-ink-muted">
            Creating the DoA now pulls the same staff list from study onboarding and files version
            1.0 as a Draft. You can then tick the responsibility grid and Generate the signing log.
          </p>
          {canCreate ? (
            <button
              type="button"
              onClick={() => create.mutate()}
              disabled={create.isPending}
              className="mt-6 btn-primary"
            >
              {create.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Create first DoA from onboarding
            </button>
          ) : (
            <div className="mt-6 inline-flex items-center gap-1.5 rounded border border-surface-border bg-white px-4 h-10 text-sm text-ink-muted">
              <Lock className="h-3.5 w-3.5" />
              Read-only role — ask a coordinator to create the DoA
            </div>
          )}
          <p className="mt-4 text-[11px] text-ink-muted">
            Requires at least one delegated staff member on the study. If none are delegated, add
            them in Study Onboarding first.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Punch-list DT2-BLIND-5 — blinded/unblinded designation on a DoA row.
 * Rendered only on blinded studies; `blinded` null means no designation
 * has been chosen yet.
 *
 * READ-ONLY (client, 16 Oct 2026). The designation is the person's blinding
 * for the WHOLE study, not a DoA setting: changing it here changed their
 * access to binders, subjects and eSource at once. It is set when the person
 * is added to the study (Assign Staff); the grid only shows it. `disabled`
 * and `onChange` are kept for the call sites and ignored.
 */
function BlindedToggle({
  name,
  blinded,
}: {
  name: string;
  blinded: boolean | null;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
}) {
  const options: Array<{ label: string; value: boolean; cls: string }> = [
    { label: 'Blinded', value: true, cls: 'bg-amber-50 text-amber-800' },
    { label: 'Unblinded', value: false, cls: 'bg-slate-100 text-slate-700' },
  ];
  return (
    <span
      role="radiogroup"
      aria-label="Blinding designation"
      aria-required="true"
      className="inline-flex items-center gap-1 text-[10px] font-medium"
    >
      {options.map((o) => {
        const selected = blinded === o.value;
        return (
          <label
            key={o.label}
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 cursor-default ${
              selected ? o.cls : 'text-ink-muted opacity-70'
            }`}
            title="Set when the person is added to the study (Assign Staff). Read-only here."
          >
            <input
              type="radio"
              name={name}
              value={o.label.toLowerCase()}
              checked={selected}
              disabled
              readOnly
              className="h-3 w-3"
            />
            {o.value && <EyeOff className="h-2.5 w-2.5" />}
            {o.label}
          </label>
        );
      })}
      {blinded === null && (
        <span className="text-danger-700" title="Set the person's Blinded/Unblinded designation on the study team (Assign Staff)">
          * Not set
        </span>
      )}
    </span>
  );
}

function DateChip({ iso, placeholder }: { iso: string | null; placeholder?: string }) {
  if (!iso) {
    return (
      <div className="inline-flex items-center gap-2 rounded border border-surface-border bg-white px-2.5 py-1 text-[11.5px] text-ink-muted min-w-[160px]">
        {placeholder ?? '—'}
        <Calendar className="ml-auto h-3 w-3 text-ink-muted" />
      </div>
    );
  }
  const formatted = fmtDate(iso);
  return (
    <div className="inline-flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11.5px] text-ink-secondary min-w-[160px]">
      {formatted}
      <Calendar className="ml-auto h-3 w-3 text-amber-700" />
    </div>
  );
}

/**
 * Feedback (2026-09-29, DoA item 1) — "end date should be editable on the
 * grid and the card". It was a read-only chip in both views, so rolling a
 * staff member off meant calling the API by hand. PATCH
 * .../doa/versions/{v}/rows/{row_id} has always accepted `effective_to`
 * (explicit null clears it); nothing on this page ever called it.
 *
 * Scope, deliberately narrow: the BE refuses ANY row edit once the version
 * leaves 'draft' (handlers/doa/updateRow.ts:78), and a signed row's snapshot
 * has to keep matching the envelope that was dispatched for signature. So
 * this is editable exactly where `isLocked` is already false — a draft
 * version's unsigned rows. Rolling someone off an ACTIVE version is a
 * different operation and still goes through services/doa/amendmentTrigger.ts,
 * which stamps effective_to itself as part of raising the amendment.
 */
function EditableDateChip({
  iso,
  onChange,
  disabled,
  placeholder,
  saving,
}: {
  iso: string | null;
  onChange: (iso: string | null) => void;
  disabled: boolean;
  placeholder?: string;
  saving?: boolean;
}) {
  if (disabled) return <DateChip iso={iso} placeholder={placeholder} />;
  return (
    <input
      type="date"
      // BE takes z.string().datetime(); <input type="date"> emits YYYY-MM-DD.
      // End-of-day matches AddStaffRowDialog's toIsoEnd so a row added there
      // and a row edited here carry the same instant for the same calendar day.
      value={iso ? iso.slice(0, 10) : ''}
      disabled={saving}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v ? new Date(`${v}T23:59:59Z`).toISOString() : null);
      }}
      title="Set the date this delegation ends — clear it to leave the row open"
      className={`inline-flex min-w-[160px] items-center gap-2 rounded border px-2.5 py-1 text-[11.5px] outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200 disabled:opacity-50 ${
        iso
          ? 'border-amber-200 bg-amber-50 text-ink-secondary'
          : 'border-surface-border bg-white text-ink-muted'
      }`}
    />
  );
}

function QualifyingDocRow({ d }: { d: DoaQualifyingDoc }) {
  const labelMeta: Record<DoaQualifyingDoc['status_label'], { label: string; cls: string }> = {
    current: { label: 'Current', cls: 'bg-emerald-50 text-emerald-700' },
    expiring: { label: 'Expiring', cls: 'bg-amber-50 text-amber-700' },
    expired: { label: 'Expired', cls: 'bg-danger-50 text-danger-700' },
    no_expiry: { label: 'No expiry', cls: 'bg-slate-50 text-slate-700' },
  };
  const m = labelMeta[d.status_label];
  return (
    <li className="flex items-center justify-between gap-2 rounded border border-surface-border px-2.5 py-1.5 text-xs">
      <div className="min-w-0">
        <div className="truncate text-ink-primary">{d.document_type_name}</div>
        {d.reference_number && (
          <div className="font-mono text-[10px] text-ink-muted">{d.reference_number}</div>
        )}
        {d.slot_expires_at && (
          <div className="text-[10px] text-ink-muted">
            Expires {new Date(d.slot_expires_at).toLocaleDateString()}
          </div>
        )}
      </div>
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${m.cls}`}>{m.label}</span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Feedback #20 - per-staff Cards view. Alternative to the matrix layout.
// One card per staff member. Header shows name + role + qualification chip.
// Body: the full responsibility list as a checklist, with an inline filter
// so tenants with 20+ responsibilities stay usable. Signed / locked rows
// render read-only.
// ---------------------------------------------------------------------------

function StaffDelegationCard({
  row,
  responsibilities,
  ticks,
  isEditable,
  showBlinding,
  blinded,
  onToggle,
  onBlindedChange,
  onOpenQual,
  onEndDateChange,
  endDateSaving,
}: {
  row: DoaGridRow;
  responsibilities: Array<{ code: string; label: string; definition: string }>;
  ticks: Set<string>;
  isEditable: boolean;
  showBlinding: boolean;
  blinded: boolean | null;
  onToggle: (code: string) => void;
  onBlindedChange: (value: boolean) => void;
  onOpenQual: () => void;
  onEndDateChange: (iso: string | null) => void;
  endDateSaving: boolean;
}) {
  const [filter, setFilter] = useState('');
  const isLocked = !isEditable || row.row_status === 'signed';
  const qualMeta = QUAL_META[row.qualification.status];

  const visible = filter.trim()
    ? responsibilities.filter((r) =>
        (r.label + ' ' + r.code + ' ' + r.definition)
          .toLowerCase()
          .includes(filter.trim().toLowerCase()),
      )
    : responsibilities;

  function selectAll() {
    if (isLocked) return;
    for (const r of visible) if (!ticks.has(r.code)) onToggle(r.code);
  }
  function clearAll() {
    if (isLocked) return;
    for (const r of visible) if (ticks.has(r.code)) onToggle(r.code);
  }

  return (
    <div className="rounded-lg border border-surface-border bg-white shadow-sm">
      <div className="flex items-start gap-2 border-b border-surface-border px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-ink-primary">
              {row.staff_full_name || row.staff_email}
            </span>
            {showBlinding && (
              <BlindedToggle
                name={`blinded-card-${row.doa_row_id}`}
                blinded={blinded}
                disabled={isLocked}
                onChange={onBlindedChange}
              />
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-ink-muted">
            {humanRole(row.role)} · {row.staff_email}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[10px] text-ink-muted">
            <span className="uppercase tracking-wide">Start</span>
            <DateChip iso={row.effective_from} />
            <span className="ml-1 uppercase tracking-wide">End</span>
            <EditableDateChip
              iso={row.effective_to}
              disabled={isLocked}
              placeholder="—"
              saving={endDateSaving}
              onChange={onEndDateChange}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenQual}
          className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${qualMeta.cls}`}
          title={qualMeta.tooltip}
        >
          {qualMeta.icon}
          {qualMeta.label}
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-surface-border bg-surface-muted/30 px-3 py-1.5">
        <div className="text-[11px] text-ink-muted">
          <span className="font-semibold text-ink-secondary">{ticks.size}</span> of{' '}
          {responsibilities.length} delegated
        </div>
        {row.row_status === 'signed' && (
          <span className="ml-2 inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
            <Lock className="h-2.5 w-2.5" /> Signed
          </span>
        )}
        {isEditable && !isLocked && (
          <div className="ml-auto flex items-center gap-1.5 text-[11px]">
            <button
              type="button"
              onClick={selectAll}
              className="text-accent-700 hover:text-accent-800"
            >
              Select all
            </button>
            <span className="text-ink-muted">·</span>
            <button
              type="button"
              onClick={clearAll}
              className="text-ink-muted hover:text-ink-primary"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="border-b border-surface-border px-3 py-1.5">
        <div className="flex items-center gap-2 rounded border border-surface-border bg-white px-2 h-7">
          <Search className="h-3 w-3 text-ink-muted shrink-0" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter responsibilities…"
            className="flex-1 bg-transparent outline-none text-[12px] text-ink-primary placeholder:text-ink-muted"
          />
        </div>
      </div>

      <ul className="max-h-[360px] overflow-auto py-1">
        {visible.length === 0 ? (
          <li className="px-3 py-4 text-center text-[11px] text-ink-muted">
            No responsibilities match "{filter}"
          </li>
        ) : (
          visible.map((r) => {
            const checked = ticks.has(r.code);
            return (
              <li key={r.code}>
                <button
                  type="button"
                  onClick={() => !isLocked && onToggle(r.code)}
                  disabled={isLocked}
                  className={`flex w-full items-start gap-2 px-3 py-1.5 text-left text-[13px] ${
                    isLocked ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent-50'
                  }`}
                >
                  <div
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      checked
                        ? 'border-emerald-600 bg-emerald-600 text-white'
                        : 'border-surface-border bg-white'
                    }`}
                  >
                    {checked && <Check className="h-2.5 w-2.5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-ink-primary">{r.label}</div>
                    {r.definition && (
                      <div className="mt-0.5 text-[11px] text-ink-muted line-clamp-2">
                        {r.definition}
                      </div>
                    )}
                  </div>
                  <span className="mt-0.5 font-mono text-[10px] text-ink-muted">{r.code}</span>
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
